import { useEffect } from "react";

import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";

import type { UserDTO } from "../../common/types/UserDTO";
import { setLaunchDarklyStatus } from "../utils/launchDarklyStatus";
import toUserContext from "../utils/toUserContext";

// identify has no request timeout of its own; past this the flags are treated as
// unavailable rather than left loading, so an outage can't hold UI hidden forever.
export const LD_IDENTIFY_TIMEOUT_MS = 5000;

export interface FeatureFlagState {
  almanac?: boolean;
  showRexFileUpload?: boolean;
  allowThinkingExpansion?: boolean;
  showEditChart?: boolean;
  manualUploadDatasetDialog?: boolean;
  showForecasts?: boolean;
  showWorkflows?: boolean;
  showSkills?: boolean;
  appMaintenance?: boolean;
  collapseToolActivity?: boolean;
  showSkillToolUi?: boolean;
  restrictUserSkillWrite?: boolean;
  showSkillArt?: boolean;
}

const useLaunchDarkly = (userData: UserDTO | undefined) => {
  const ldClient = useLDClient();
  const flags = useFlags<FeatureFlagState>();

  useEffect(() => {
    if (!ldClient) {
      setLaunchDarklyStatus("unavailable");
      return;
    }

    const identified = ldClient.identify(toUserContext(userData));
    // Until the user is known the flags are the anonymous bootstrap's, never theirs.
    if (!userData) {
      void identified.catch(() => {});
      return;
    }

    let current = true;
    setLaunchDarklyStatus("loading");
    const timer = setTimeout(() => {
      if (current) setLaunchDarklyStatus("unavailable");
    }, LD_IDENTIFY_TIMEOUT_MS);
    // A late answer still counts: once LaunchDarkly recovers the flags are the user's.
    identified.then(
      () => {
        clearTimeout(timer);
        if (current) setLaunchDarklyStatus("ready");
      },
      () => {
        clearTimeout(timer);
        if (current) setLaunchDarklyStatus("unavailable");
      },
    );

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [ldClient, userData]);

  if (!ldClient) return {};

  return { flags };
};

export default useLaunchDarkly;
